import axios, { AxiosRequestConfig, AxiosError } from 'axios';
import {
  ApiResponse,
  Checklist,
  ChecklistItem,
  ChecklistStats,
  Vehicle,
  Driver,
  UploadResponse,
  LoginRequest,
  LoginResponse,
  RefreshTokenResponse,
  PaginationParams
} from '../types/api';
import { Config, getEndpointUrl, getDefaultHeaders, getToken, setToken } from './config';
import { ApiEndpoints } from '../types/config';
import { getConferenteToken, handleConferenteSessionExpired } from './conferenteAuth';

/**
 * Serviço de API para comunicação com o backend
 */
class ApiService {
  private isRefreshing = false;
  private failedQueue: Array<{ resolve: (value: any) => void; reject: (error: any) => void }> = [];

  /**
   * Faz uma requisição HTTP para a API usando Axios
   */
  async request<T>(url: string, options: RequestInit = {}): Promise<T> {
    try {
      // Obter headers padrão (inclui Authorization com token)
      const defaultHeaders = getDefaultHeaders();

      // Preparar dados do body
      let bodyData: any;
      if (options.body) {
        // Se for FormData, usar diretamente
        if (options.body instanceof FormData) {
          bodyData = options.body;
          // Para FormData, remover Content-Type para deixar o axios configurar automaticamente
          delete defaultHeaders['Content-Type'];
        } else if (typeof options.body === 'string') {
          // Se for string, tentar parsear como JSON
          try {
            bodyData = JSON.parse(options.body);
          } catch {
            bodyData = options.body;
          }
        } else {
          bodyData = options.body;
        }
      }

      // Merge headers
      const mergedHeaders: any = {
        ...defaultHeaders,
        ...(options.headers || {})
      };

      // Configuração do Axios
      const axiosConfig: AxiosRequestConfig = {
        url,
        method: (options.method as string) || 'GET',
        headers: mergedHeaders,
        timeout: Config.TIMEOUTS.API_REQUEST,
        data: bodyData
      };

      const response = await axios(axiosConfig);

      const data = response.data;

      // Verificar se a resposta já está no formato ApiResponse (inglês ou português)
      if (data && typeof data === 'object' && ('success' in data || 'sucesso' in data)) {
        // Se for formato português, normalizar para inglês
        if ('sucesso' in data && !('success' in data)) {
          return {
            ...data, // Preserva todos os campos originais (como paginacao)
            success: data.sucesso,
            message: data.mensagem || data.message || 'Sucesso',
            data: data.dados || data.data,
            // Normalizar paginacao -> pagination
            pagination: data.paginacao || data.pagination
          } as T;
        }
        return data as T;
      }

      // Se não estiver no formato ApiResponse, converter para o formato esperado
      return {
        success: true,
        message: 'Sucesso',
        data: data
      } as T;

    } catch (error) {
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;

        if (axiosError.code === 'ECONNABORTED') {
          throw new Error(Config.MESSAGES.ERRORS.TIMEOUT);
        }

        if (axiosError.response) {
          throw await this.handleAxiosError(axiosError);
        }
      }

      throw this.handleRequestError(error as Error);
    }
  }

  /**
   * Manipula erros da API usando Axios
   */
  private async handleAxiosError(error: AxiosError): Promise<Error> {
    let errorMessage: string;

    const response = error.response;

    if (!response) {
      return new Error(Config.MESSAGES.ERRORS.NETWORK);
    }

    // Se for 401 e o usuário ativo é um conferente, redirecionar silenciosamente
    // em vez de exibir mensagem de erro confusa
    if (response.status === 401 && getConferenteToken()) {
      handleConferenteSessionExpired();
      // Retorna uma promise que nunca resolve para evitar exibição de erro na UI
      return new Promise(() => {});
    }

    try {
      // Tentar obter detalhes do erro da resposta
      const errorData = response.data as any;
      console.error('Detalhes do erro da API:', errorData);

      if (errorData.errors && Array.isArray(errorData.errors)) {
        errorMessage = errorData.errors.join(', ');
      } else if (errorData.mensagem) {
        // Backend usa 'mensagem' (PT-BR)
        errorMessage = errorData.mensagem;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else if (errorData.error) {
        errorMessage = errorData.error;
      } else {
        errorMessage = response.statusText || 'Erro ao processar a requisição';
      }
    } catch (parseError) {
      // Se não conseguir parsear, usar status padrão
      switch (response.status) {
        case 401:
          errorMessage = Config.MESSAGES.ERRORS.UNAUTHORIZED;
          setTimeout(() => this.handleTokenExpiration(), 2000);
          break;
        case 403:
          errorMessage = Config.MESSAGES.ERRORS.FORBIDDEN;
          break;
        case 404:
          errorMessage = Config.MESSAGES.ERRORS.NOT_FOUND;
          break;
        case 422:
          errorMessage = Config.MESSAGES.ERRORS.VALIDATION;
          break;
        case 500:
          errorMessage = Config.MESSAGES.ERRORS.SERVER;
          break;
        default:
          errorMessage = response.statusText || 'Erro ao processar a requisição';
      }
    }

    return new Error(errorMessage);
  }

  /**
   * Manipula erros de requisição
   */
  private handleRequestError(error: Error): Error {
    console.error('❌ Erro na requisição API:', error);
    
    if (error.message.includes('Failed to fetch')) {
      return new Error(Config.MESSAGES.ERRORS.NETWORK);
    }
    
    return error;
  }

  /**
   * Manipula expiração do token
   */
  private async handleTokenExpiration(): Promise<void> {
    if (this.isRefreshing) return;

    this.isRefreshing = true;
    
    try {
      const response = await this.refreshToken();
      
      if (response.success && response.token) {
        setToken(response.token);
        this.processQueue(null, response.token);
      } else {
        this.processQueue(new Error('Falha ao renovar token'), null);
      }
    } catch (error) {
      this.processQueue(error as Error, null);
    } finally {
      this.isRefreshing = false;
    }
  }

  /**
   * Processa a fila de requisições falhas
   */
  private processQueue(error: Error | null, token: string | null): void {
    this.failedQueue.forEach(prom => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    });
    
    this.failedQueue = [];
  }

  /**
   * Faz uma requisição GET
   */
  async get<T>(endpointKey: keyof ApiEndpoints, params: Record<string, string | number> = {}, queryParams: Record<string, string | number | undefined> = {}): Promise<T> {
    let url = getEndpointUrl(endpointKey, params);

    // Adicionar query parameters se existirem (filtrando undefined)
    const filteredQueryParams = Object.fromEntries(
      Object.entries(queryParams).filter(([_, value]) => value !== undefined)
    ) as Record<string, string | number>;

    if (Object.keys(filteredQueryParams).length > 0) {
      const queryString = new URLSearchParams();
      for (const [key, value] of Object.entries(filteredQueryParams)) {
        queryString.append(key, value.toString());
      }
      url += `?${queryString.toString()}`;
    }

    return this.request<T>(url, { method: 'GET' });
  }

  /**
   * Faz uma requisição GET customizada (para URLs dinâmicas)
   */
  async getCustom<T>(path: string): Promise<T> {
    const url = Config.API_BASE_URL + path;
    return this.request<T>(url, { method: 'GET' });
  }

  /**
   * Faz uma requisição POST customizada (para URLs dinâmicas)
   */
  async postCustom<T>(path: string, data: any = {}): Promise<T> {
    const url = Config.API_BASE_URL + path;
    return this.request<T>(url, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  /**
   * Faz uma requisição PUT customizada (para URLs dinâmicas)
   */
  async putCustom<T>(path: string, data: any = {}): Promise<T> {
    const url = Config.API_BASE_URL + path;
    return this.request<T>(url, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  /**
   * Faz uma requisição POST
   */
  async post<T>(endpointKey: keyof ApiEndpoints, data: any = {}, params: Record<string, string | number> = {}): Promise<T> {
    const url = getEndpointUrl(endpointKey, params);

    return this.request<T>(url, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  /**
   * Faz uma requisição PUT
   */
  async put<T>(endpointKey: keyof ApiEndpoints, data: any = {}, params: Record<string, string | number> = {}): Promise<T> {
    const url = getEndpointUrl(endpointKey, params);

    return this.request<T>(url, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  /**
   * Faz uma requisição PATCH
   */
  async patch<T>(endpointKey: keyof ApiEndpoints, data: any = {}, params: Record<string, string | number> = {}): Promise<T> {
    const url = getEndpointUrl(endpointKey, params);

    return this.request<T>(url, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  }

  /**
   * Faz uma requisição DELETE
   */
  async delete<T>(endpointKey: keyof ApiEndpoints, params: Record<string, string | number> = {}): Promise<T> {
    const url = getEndpointUrl(endpointKey, params);

    return this.request<T>(url, { method: 'DELETE' });
  }

  /**
   * Faz upload de arquivo
   */
  async upload(file: File, type: string = 'generic'): Promise<UploadResponse> {
    const url = getEndpointUrl('UPLOADS');
    const formData = new FormData();
    
    formData.append('file', file);
    formData.append('type', type);
    
    const headers: Record<string, string> = {
      'Authorization': `Bearer ${getToken()}`
    };
    
    return this.request<UploadResponse>(url, {
      method: 'POST',
      headers: headers,
      body: formData
    });
  }

  // =============================================
  // MÉTODOS ESPECÍFICOS DA APLICAÇÃO
  // =============================================

  /**
   * Realiza login
   */
  async login(loginData: LoginRequest): Promise<LoginResponse> {
    return this.post<LoginResponse>('LOGIN', loginData);
  }

  /**
   * Obtém informações do usuário autenticado
   */
  async getCurrentUser(): Promise<ApiResponse<{ id: number; nome: string; email: string; tipo: string }>> {
    return this.get<ApiResponse<{ id: number; nome: string; email: string; tipo: string }>>('USER_INFO');
  }

  /**
   * Obtém lista de checklists
   */
  async getChecklists(filters: PaginationParams = {}): Promise<ApiResponse<{ checklists: Checklist[]; total: number }>> {
    return this.get<ApiResponse<{ checklists: Checklist[]; total: number }>>('CHECKLISTS', {}, filters);
  }

  /**
   * Obtém checklist por ID
   */
  async getChecklist(id: number): Promise<ApiResponse<Checklist>> {
    return this.get<ApiResponse<Checklist>>('CHECKLIST_BY_ID', { id });
  }

  /**
   * Obtém itens de um checklist
   */
  async getChecklistItems(checklistId: number): Promise<ApiResponse<ChecklistItem[]>> {
    return this.get<ApiResponse<ChecklistItem[]>>('CHECKLIST_ITEMS', { id: checklistId });
  }

  /**
   * Cria um novo checklist
   */
  async createChecklist(checklistData: Partial<Checklist>): Promise<ApiResponse<Checklist>> {
    return this.post<ApiResponse<Checklist>>('CHECKLISTS', checklistData);
  }

  /**
   * Atualiza um checklist
   */
  async updateChecklist(id: number, updateData: Partial<Checklist>): Promise<ApiResponse<Checklist>> {
    return this.put<ApiResponse<Checklist>>('CHECKLIST_BY_ID', updateData, { id });
  }

  /**
   * Deleta um checklist
   */
  async deleteChecklist(id: number): Promise<ApiResponse<{ message: string }>> {
    return this.delete<ApiResponse<{ message: string }>>('CHECKLIST_BY_ID', { id });
  }

  /**
   * Atualiza um item específico de um checklist
   */
  async updateChecklistItemInChecklist(checklistId: number, itemId: number, updateData: { valor: string; observacao?: string }): Promise<ApiResponse<ChecklistItem>> {
    return this.put<ApiResponse<ChecklistItem>>('CHECKLIST_ITEM_BY_CHECKLIST', updateData, { checklistId, itemId });
  }

  /**
   * Cria um novo item em um checklist (durante edição)
   */
  async createChecklistItemForChecklist(
    checklistId: number,
    itemData: {
      categoria: string;
      descricao: string;
      tipo?: string;
      obrigatorio?: boolean;
      valor?: string;
      observacao?: string;
    }
  ): Promise<ApiResponse<ChecklistItem>> {
    // Backend espera um array de itens dentro do campo 'itens'
    const payload = { itens: [itemData] };
    return this.post<ApiResponse<ChecklistItem>>('CHECKLIST_ITEMS', payload, { id: checklistId });
  }

  /**
   * Deleta um item de um checklist (durante edição)
   */
  async deleteChecklistItemFromChecklist(checklistId: number, itemId: number): Promise<ApiResponse<{ message: string }>> {
    return this.delete<ApiResponse<{ message: string }>>('CHECKLIST_ITEM_BY_CHECKLIST', { checklistId, itemId });
  }

  /**
   * Obtém estatísticas de checklists
   */
  async getChecklistStats(periodo: string = 'mes'): Promise<ApiResponse<ChecklistStats>> {
    return this.get<ApiResponse<ChecklistStats>>('CHECKLIST_STATS', {}, { periodo });
  }

  /**
   * Obtém lista de itens padrão de checklist (não associados a nenhum checklist)
   */
  async getChecklistItemsDefault(categoria?: string, search?: string): Promise<ApiResponse<ChecklistItem[]>> {
    return this.get<ApiResponse<ChecklistItem[]>>('CHECKLIST_ITEMS_DEFAULT', {}, { categoria, search });
  }

  /**
   * Obtém lista de itens de checklist (modelos/itens padrão)
   */
  async getChecklistItemsManagement(): Promise<ApiResponse<{ itens: ChecklistItem[]; pagination: any }>> {
    return this.get<ApiResponse<{ itens: ChecklistItem[]; pagination: any }>>('CHECKLIST_ITEMS_MANAGEMENT');
  }

  /**
   * Obtém item de checklist por ID (modelos/itens padrão)
   */
  async getChecklistItemById(id: number): Promise<ApiResponse<ChecklistItem>> {
    return this.get<ApiResponse<ChecklistItem>>('CHECKLIST_ITEM_BY_ID', { id });
  }

  /**
   * Cria um novo item de checklist (modelos/itens padrão)
   */
  async createChecklistItem(itemData: Partial<ChecklistItem>): Promise<ApiResponse<ChecklistItem>> {
    return this.post<ApiResponse<ChecklistItem>>('CHECKLIST_ITEMS_MANAGEMENT', itemData);
  }

  /**
   * Atualiza um item de checklist (modelos/itens padrão)
   */
  async updateChecklistItem(id: number, updateData: Partial<ChecklistItem>): Promise<ApiResponse<ChecklistItem>> {
    return this.put<ApiResponse<ChecklistItem>>('CHECKLIST_ITEM_BY_ID', updateData, { id });
  }

  /**
   * Deleta um item de checklist (modelos/itens padrão)
   */
  async deleteChecklistItem(id: number): Promise<ApiResponse<{ message: string }>> {
    return this.delete<ApiResponse<{ message: string }>>('CHECKLIST_ITEM_BY_ID', { id });
  }

  /**
   * Obtém estatísticas de itens de checklist
   */
  async getChecklistItemsStats(): Promise<ApiResponse<any>> {
    return this.get<ApiResponse<any>>('CHECKLIST_ITEMS_STATS');
  }

  /**
   * Obtém lista de grupos de checklist
   */
  async getChecklistGroups(filters: PaginationParams & { tipo?: string; status?: string; search?: string } = {}): Promise<ApiResponse<{ grupos: any[]; pagination: any }>> {
    return this.get<ApiResponse<{ grupos: any[]; pagination: any }>>('CHECKLIST_GROUPS', {}, filters);
  }

  /**
   * Obtém grupo de checklist por ID
   */
  async getChecklistGroup(id: number): Promise<ApiResponse<any>> {
    return this.get<ApiResponse<any>>('CHECKLIST_GROUP_BY_ID', { id });
  }

  /**
   * Obtém estatísticas de grupos de checklist
   */
  async getChecklistGroupStats(): Promise<ApiResponse<any>> {
    return this.get<ApiResponse<any>>('CHECKLIST_GROUP_STATS');
  }

  /**
   * Obtém grupos de checklist por tipo
   */
  async getChecklistGroupsByType(tipo: string): Promise<ApiResponse<any[]>> {
    return this.get<ApiResponse<any[]>>('CHECKLIST_GROUP_BY_TYPE', { tipo });
  }

  /**
   * Obtém lista de agendamentos
   */
  async getSchedules(filters: PaginationParams & { status?: string; tipo?: string } = {}): Promise<ApiResponse<{ agendamentos: any[]; pagination: any }>> {
    return this.get<ApiResponse<{ agendamentos: any[]; pagination: any }>>('SCHEDULES', {}, filters);
  }

  /**
   * Obtém agendamento por ID
   */
  async getSchedule(id: string): Promise<ApiResponse<any>> {
    return this.get<ApiResponse<any>>('SCHEDULE_BY_ID', { id });
  }

  /**
   * Cria um novo agendamento
   */
  async createSchedule(scheduleData: any): Promise<ApiResponse<any>> {
    return this.post<ApiResponse<any>>('SCHEDULES', scheduleData);
  }

  /**
   * Atualiza um agendamento
   */
  async updateSchedule(id: string, updateData: any): Promise<ApiResponse<any>> {
    return this.put<ApiResponse<any>>('SCHEDULE_BY_ID', updateData, { id });
  }

  /**
   * Deleta um agendamento
   */
  async deleteSchedule(id: string): Promise<ApiResponse<{ message: string }>> {
    return this.delete<ApiResponse<{ message: string }>>('SCHEDULE_BY_ID', { id });
  }

  /**
   * Cria um novo grupo de checklist
   */
  async createChecklistGroup(groupData: any): Promise<ApiResponse<any>> {
    return this.post<ApiResponse<any>>('CHECKLIST_GROUPS', groupData);
  }

  /**
   * Atualiza um grupo de checklist
   */
  async updateChecklistGroup(id: number, updateData: any): Promise<ApiResponse<any>> {
    return this.put<ApiResponse<any>>('CHECKLIST_GROUP_BY_ID', updateData, { id });
  }

  /**
   * Deleta um grupo de checklist
   */
  async deleteChecklistGroup(id: number): Promise<ApiResponse<{ message: string }>> {
    return this.delete<ApiResponse<{ message: string }>>('CHECKLIST_GROUP_BY_ID', { id });
  }

  /**
   * Obtém itens vinculados a um grupo de checklist
   */
  async getChecklistGroupItems(groupId: number): Promise<ApiResponse<any>> {
    return this.get<ApiResponse<any>>('CHECKLIST_GROUP_ITEMS', { id: groupId });
  }

  /**
   * Obtém itens disponíveis para vincular a um grupo
   */
  async getChecklistGroupAvailableItems(groupId: number, filters: { search?: string; categoria?: string } = {}): Promise<ApiResponse<any>> {
    return this.get<ApiResponse<any>>('CHECKLIST_GROUP_ITEMS_AVAILABLE', { id: groupId }, filters);
  }

  /**
   * Vincula um item a um grupo de checklist
   */
  async addItemToGroup(groupId: number, itemId: number, ordem: number = 0): Promise<ApiResponse<any>> {
    return this.post<ApiResponse<any>>('CHECKLIST_GROUP_ITEMS', { item_id: itemId, ordem }, { id: groupId });
  }

  /**
   * Desvincula um item de um grupo de checklist
   */
  async removeItemFromGroup(groupId: number, itemId: number): Promise<ApiResponse<any>> {
    return this.delete<ApiResponse<any>>('CHECKLIST_GROUP_ITEM_BY_ID', { id: groupId, itemId });
  }

  /**
   * Define todos os itens de um grupo (substitui existentes)
   */
  async setGroupItems(groupId: number, itens: Array<{ itemId: number; ordem: number }>): Promise<ApiResponse<any>> {
    return this.put<ApiResponse<any>>('CHECKLIST_GROUP_ITEMS', { itens }, { id: groupId });
  }

  /**
   * Obtém os grupos que contêm um item específico
   */
  async getGroupsByItem(itemId: number): Promise<ApiResponse<any>> {
    return this.get<ApiResponse<any>>('CHECKLIST_GROUPS_BY_ITEM', { itemId });
  }

  /**
   * Atualiza a ordem de um item dentro de um grupo
   */
  async updateItemOrderInGroup(groupId: number, itemId: number, ordem: number): Promise<ApiResponse<any>> {
    return this.patch<ApiResponse<any>>('CHECKLIST_GROUP_ITEM_ORDER', { ordem }, { id: groupId, itemId });
  }

  /**
   * Obtém lista de veículos
   */
  async getVehicles(filters: PaginationParams & { status?: string } = {}): Promise<ApiResponse<Vehicle[]>> {
    return this.get<ApiResponse<Vehicle[]>>('VEHICLES', {}, filters);
  }

  /**
   * Obtém veículo por ID
   */
  async getVehicle(id: number): Promise<ApiResponse<Vehicle>> {
    return this.get<ApiResponse<Vehicle>>('VEHICLE_BY_ID', { id });
  }

  /**
   * Cria um novo veículo
   */
  async createVehicle(vehicleData: Partial<Vehicle>): Promise<ApiResponse<Vehicle>> {
    return this.post<ApiResponse<Vehicle>>('VEHICLES', vehicleData);
  }

  /**
   * Atualiza um veículo existente
   */
  async updateVehicle(id: number, updateData: Partial<Vehicle>): Promise<ApiResponse<Vehicle>> {
    return this.put<ApiResponse<Vehicle>>('VEHICLE_BY_ID', updateData, { id });
  }

  /**
   * Gera QR Code para identificação do veículo
   */
  async getVehicleQRCode(id: number): Promise<ApiResponse<{ qrcode: string; veiculo: { id: number; placa: string; tag: string; marca: string; modelo: string }; qr_data: string }>> {
    return this.getCustom<ApiResponse<{ qrcode: string; veiculo: { id: number; placa: string; tag: string; marca: string; modelo: string }; qr_data: string }>>(`/veiculos/${id}/qrcode`);
  }

  /**
   * Obtém lista de motoristas
   */
  async getDrivers(filters: PaginationParams & { status?: string } = {}): Promise<ApiResponse<Driver[]>> {
    return this.get<ApiResponse<Driver[]>>('DRIVERS', {}, filters);
  }

  /**
   * Obtém motorista por ID
   */
  async getDriver(id: number): Promise<ApiResponse<Driver>> {
    return this.get<ApiResponse<Driver>>('DRIVER_BY_ID', { id });
  }

  /**
   * Cria um novo motorista
   */
  async createDriver(driverData: Partial<Driver>): Promise<ApiResponse<Driver>> {
    return this.post<ApiResponse<Driver>>('DRIVERS', driverData);
  }

  /**
   * Atualiza um motorista existente
   */
  async updateDriver(id: number, updateData: Partial<Driver>): Promise<ApiResponse<Driver>> {
    return this.put<ApiResponse<Driver>>('DRIVER_BY_ID', updateData, { id });
  }

  /**
   * Obtém lista de abastecimentos
   */
  async getFuelings(filters: PaginationParams & { status?: string; veiculo_id?: number; motorista_id?: number } = {}): Promise<ApiResponse<any[]>> {
    return this.get<ApiResponse<any[]>>('FUELINGS', {}, filters);
  }

  /**
   * Obtém abastecimento por ID
   */
  async getFueling(id: number): Promise<ApiResponse<any>> {
    return this.get<ApiResponse<any>>('FUELING_BY_ID', { id });
  }

  /**
   * Cria um novo abastecimento
   */
  async createFueling(fuelingData: any): Promise<ApiResponse<any>> {
    return this.post<ApiResponse<any>>('FUELINGS', fuelingData);
  }

  /**
   * Atualiza um abastecimento existente
   */
  async updateFueling(id: number, updateData: any): Promise<ApiResponse<any>> {
    return this.put<ApiResponse<any>>('FUELING_BY_ID', updateData, { id });
  }

  /**
   * Deleta um abastecimento
   */
  async deleteFueling(id: number): Promise<ApiResponse<any>> {
    return this.delete<ApiResponse<any>>('FUELING_BY_ID', { id });
  }

  /**
   * Obtém lista de equipamentos
   */
  async getEquipments(filters: PaginationParams & { status?: string } = {}): Promise<ApiResponse<any[]>> {
    return this.get<ApiResponse<any[]>>('EQUIPMENTS', {}, filters);
  }

  /**
   * Obtém equipamento por ID
   */
  async getEquipment(id: number): Promise<ApiResponse<any>> {
    return this.get<ApiResponse<any>>('EQUIPMENT_BY_ID', { id });
  }

  /**
   * Cria um novo equipamento
   */
  async createEquipment(equipmentData: any): Promise<ApiResponse<any>> {
    return this.post<ApiResponse<any>>('EQUIPMENTS', equipmentData);
  }

  /**
   * Atualiza um equipamento existente
   */
  async updateEquipment(id: number, updateData: any): Promise<ApiResponse<any>> {
    return this.put<ApiResponse<any>>('EQUIPMENT_BY_ID', updateData, { id });
  }

  /**
   * Gera QR Code para identificação do equipamento
   */
  async getEquipmentQRCode(id: number): Promise<ApiResponse<{ qrcode: string; equipamento: { id: number; nome: string; tag: string; tipo: string; localizacao: string }; qr_data: string }>> {
    return this.getCustom<ApiResponse<{ qrcode: string; equipamento: { id: number; nome: string; tag: string; tipo: string; localizacao: string }; qr_data: string }>>(`/equipamentos/${id}/qrcode`);
  }

  /**
   * Atualiza token de autenticação
   */
  async refreshToken(): Promise<RefreshTokenResponse> {
    return this.post<RefreshTokenResponse>('REFRESH_TOKEN');
  }

  /**
   * Solicita recuperação de senha
   */
  async forgotPassword(email: string): Promise<ApiResponse<{ message: string }>> {
    return this.post<ApiResponse<{ message: string }>>('FORGOT_PASSWORD', { email });
  }

  /**
   * Testa conexão com S3
   */
  async testS3Connection(): Promise<ApiResponse<{ connected: boolean; bucket: string }>> {
    return this.get<ApiResponse<{ connected: boolean; bucket: string }>>('UPLOADS_TEST_CONNECTION');
  }

  // =============================================
  // MÉTODOS DO DASHBOARD
  // =============================================

  /**
   * Obtém estatísticas completas do dashboard
   */
  async getDashboardStats(periodo: string = 'mes'): Promise<ApiResponse<any>> {
    return this.get<ApiResponse<any>>('DASHBOARD_STATS', {}, { periodo });
  }

  /**
   * Obtém evolução de checklists para gráficos
   */
  async getDashboardEvolucaoChecklists(periodo: string = 'mes'): Promise<ApiResponse<any>> {
    return this.get<ApiResponse<any>>('DASHBOARD_EVOLUCAO_CHECKLISTS', {}, { periodo });
  }

  /**
   * Obtém atividades recentes do sistema
   */
  async getDashboardAtividadesRecentes(limite: number = 10): Promise<ApiResponse<any>> {
    return this.get<ApiResponse<any>>('DASHBOARD_ATIVIDADES_RECENTES', {}, { limite });
  }

  /**
   * Obtém veículos com mais problemas
   */
  async getDashboardVeiculosProblemas(limite: number = 5): Promise<ApiResponse<any>> {
    return this.get<ApiResponse<any>>('DASHBOARD_VEICULOS_PROBLEMAS', {}, { limite });
  }

  // =============================================
  // MÉTODOS DE CHECKLIST PERSONALIZADO POR VEÍCULO
  // =============================================

  /**
   * Obtém lista de checklists personalizados por veículo
   */
  async getVeiculoChecklists(): Promise<ApiResponse<any[]>> {
    return this.get<ApiResponse<any[]>>('VEHICULO_CHECKLISTS');
  }

  /**
   * Obtém checklist personalizado por ID
   */
  async getVeiculoChecklist(id: number): Promise<ApiResponse<any>> {
    return this.get<ApiResponse<any>>('VEHICULO_CHECKLIST_BY_ID', { id });
  }

  /**
   * Obtém checklist personalizado por veículo
   */
  async getVeiculoChecklistByVehicle(veiculoId: number): Promise<ApiResponse<any>> {
    return this.get<ApiResponse<any>>('VEHICULO_CHECKLIST_BY_VEHICLE', { id: veiculoId });
  }

  /**
   * Obtém JSON estruturado do checklist personalizado por veículo
   */
  async getVeiculoChecklistJson(veiculoId: number): Promise<ApiResponse<any>> {
    return this.get<ApiResponse<any>>('VEHICULO_CHECKLIST_JSON', { id: veiculoId });
  }

  /**
   * Cria um novo checklist personalizado
   */
  async createVeiculoChecklist(checklistData: any): Promise<ApiResponse<any>> {
    return this.post<ApiResponse<any>>('VEHICULO_CHECKLISTS', checklistData);
  }

  /**
   * Atualiza um checklist personalizado
   */
  async updateVeiculoChecklist(id: number, updateData: any): Promise<ApiResponse<any>> {
    return this.put<ApiResponse<any>>('VEHICULO_CHECKLIST_BY_ID', updateData, { id });
  }

  /**
   * Deleta um checklist personalizado
   */
  async deleteVeiculoChecklist(id: number): Promise<ApiResponse<{ message: string }>> {
    return this.delete<ApiResponse<{ message: string }>>('VEHICULO_CHECKLIST_BY_ID', { id });
  }

  /**
   * Adiciona grupo a um checklist personalizado
   */
  async addVeiculoChecklistGroup(checklistId: number, groupData: any): Promise<ApiResponse<any>> {
    return this.post<ApiResponse<any>>('VEHICULO_CHECKLIST_GROUPS', groupData, { id: checklistId });
  }

  /**
   * Remove grupo de um checklist personalizado
   */
  async removeVeiculoChecklistGroup(checklistId: number, grupoId: number): Promise<ApiResponse<{ message: string }>> {
    return this.delete<ApiResponse<{ message: string }>>('VEHICULO_CHECKLIST_GROUPS', { id: checklistId, grupoId });
  }

  // =============================================
  // MÉTODOS DE CHECKLIST PERSONALIZADO ADAPTADO
  // =============================================

  /**
   * Obtém checklist personalizado por veículo (API adaptada)
   */
  async getVeiculoChecklistAdaptadoByVehicle(veiculoId: number): Promise<ApiResponse<any>> {
    return this.get<ApiResponse<any>>('VEHICULO_CHECKLIST_ADAPTADO_BY_VEHICLE', { id: veiculoId });
  }

  /**
   * Cria um novo checklist personalizado (API adaptada)
   */
  async createVeiculoChecklistAdaptado(checklistData: any): Promise<ApiResponse<any>> {
    return this.post<ApiResponse<any>>('VEHICULO_CHECKLISTS_ADAPTADO', checklistData);
  }

  /**
   * Atualiza um checklist personalizado (API adaptada)
   */
  async updateVeiculoChecklistAdaptado(id: number, updateData: any): Promise<ApiResponse<any>> {
    return this.put<ApiResponse<any>>('VEHICULO_CHECKLIST_ADAPTADO_BY_ID', updateData, { id });
  }

  // =============================================
  // MÉTODOS WHATSAPP
  // =============================================

  /**
   * Obtém lista de conexões WhatsApp
   */
  async getWhatsAppConnections(empresa_id?: number): Promise<ApiResponse<any[]>> {
    return this.get<ApiResponse<any[]>>('WHATSAPP_CONNECTIONS', {}, { empresa_id });
  }

  /**
   * Obtém API key de uma conexão WhatsApp
   */
  async getWhatsAppApiKey(connectionId: number): Promise<ApiResponse<{ id: number; nome: string; api_key: string }>> {
    return this.get<ApiResponse<{ id: number; nome: string; api_key: string }>>('WHATSAPP_API_KEY', { id: connectionId });
  }

  /**
   * Atualiza API key de uma conexão WhatsApp com valor customizado
   */
  async updateWhatsAppApiKey(connectionId: number, apiKey: string): Promise<ApiResponse<{ id: number; nome: string; api_key: string }>> {
    return this.put<ApiResponse<{ id: number; nome: string; api_key: string }>>('WHATSAPP_API_KEY', { api_key: apiKey }, { id: connectionId });
  }

  /**
   * Gera nova API key aleatória para uma conexão WhatsApp
   */
  async generateWhatsAppApiKey(connectionId: number): Promise<ApiResponse<{ id: number; nome: string; api_key: string }>> {
    return this.post<ApiResponse<{ id: number; nome: string; api_key: string }>>('WHATSAPP_API_KEY_GENERATE', {}, { id: connectionId });
  }

  // =============================================
  // MÉTODOS DE OCORRÊNCIAS
  // =============================================

  /**
   * Obtém lista de ocorrências
   */
  async getOccurrences(filters: PaginationParams & { status?: string; tipo?: string; gravidade?: string } = {}): Promise<ApiResponse<{ ocorrencias: any[]; pagination: any }>> {
    return this.get<ApiResponse<{ ocorrencias: any[]; pagination: any }>>('OCCURRENCES', {}, filters);
  }

  /**
   * Obtém ocorrência por ID
   */
  async getOccurrence(id: number): Promise<ApiResponse<any>> {
    return this.get<ApiResponse<any>>('OCCURRENCE_BY_ID', { id });
  }

  /**
   * Cria uma nova ocorrência
   */
  async createOccurrence(occurrenceData: any): Promise<ApiResponse<any>> {
    return this.post<ApiResponse<any>>('OCCURRENCES', occurrenceData);
  }

  /**
   * Atualiza uma ocorrência
   */
  async updateOccurrence(id: number, updateData: any): Promise<ApiResponse<any>> {
    return this.put<ApiResponse<any>>('OCCURRENCE_BY_ID', updateData, { id });
  }

  /**
   * Deleta uma ocorrência
   */
  async deleteOccurrence(id: number): Promise<ApiResponse<{ message: string }>> {
    return this.delete<ApiResponse<{ message: string }>>('OCCURRENCE_BY_ID', { id });
  }

  /**
   * Obtém estatísticas das ocorrências
   */
  async getOccurrenceStatistics(): Promise<ApiResponse<any>> {
    return this.get<ApiResponse<any>>('OCCURRENCE_STATISTICS');
  }

  /**
   * Marca ocorrência como resolvida
   */
  async resolveOccurrence(id: number, data: { observacoes_resolucao?: string; responsavel?: string }): Promise<ApiResponse<any>> {
    return this.patch<ApiResponse<any>>('OCCURRENCE_RESOLVE', data, { id });
  }

  // =============================================
  // MÉTODOS DE AUTOMAÇÃO WHATSAPP
  // =============================================

  /**
   * Lista todos os fluxos de automação
   */
  async listAutomationFlows(params?: any): Promise<ApiResponse<any>> {
    const queryString = params ? `?${new URLSearchParams(params).toString()}` : '';
    return this.request<ApiResponse<any>>(
      `${Config.API_BASE_URL}/automation/flows${queryString}`,
      { method: 'GET' }
    );
  }

  /**
   * Obtém fluxo de automação por ID
   */
  async getAutomationFlow(id: number): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>(
      `${Config.API_BASE_URL}/automation/flows/${id}`,
      { method: 'GET' }
    );
  }

  /**
   * Timeline de execuções de automação (dados reais do backend)
   */
  async getAutomationTimeline(params?: { interval?: string; data_inicio?: string; data_fim?: string; flow_id?: number }): Promise<ApiResponse<any>> {
    const queryString = params
      ? new URLSearchParams(
          Object.entries(params)
            .filter(([_, value]) => value !== undefined)
            .map(([key, value]) => [key, String(value)])
        ).toString()
      : '';
    return this.request<ApiResponse<any>>(
      `${Config.API_BASE_URL}/automation/metrics/timeline${queryString ? `?${queryString}` : ''}`,
      { method: 'GET' }
    );
  }

  /**
   * Cria novo fluxo de automação
   */
  async createAutomationFlow(flowData: any): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>(
      `${Config.API_BASE_URL}/automation/flows`,
      {
        method: 'POST',
        body: JSON.stringify(flowData)
      }
    );
  }

  /**
   * Atualiza fluxo de automação
   */
  async updateAutomationFlow(id: number, updateData: any): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>(
      `${Config.API_BASE_URL}/automation/flows/${id}`,
      {
        method: 'PUT',
        body: JSON.stringify(updateData)
      }
    );
  }

  /**
   * Deleta fluxo de automação
   */
  async deleteAutomationFlow(id: number): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>(
      `${Config.API_BASE_URL}/automation/flows/${id}`,
      { method: 'DELETE' }
    );
  }

  /**
   * Duplica fluxo de automação
   */
  async duplicateAutomationFlow(id: number): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>(
      `${Config.API_BASE_URL}/automation/flows/${id}/duplicate`,
      { method: 'POST' }
    );
  }

  /**
   * Obtém estatísticas de um fluxo
   */
  async getFlowStatistics(id: number): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>(
      `${Config.API_BASE_URL}/automation/flows/${id}/statistics`,
      { method: 'GET' }
    );
  }

  /**
   * Lista execuções de automação
   */
  async listAutomationExecutions(params?: any): Promise<ApiResponse<any>> {
    const queryString = params ? `?${new URLSearchParams(params).toString()}` : '';
    return this.request<ApiResponse<any>>(
      `${Config.API_BASE_URL}/automation/executions${queryString}`,
      { method: 'GET' }
    );
  }

  /**
   * Obtém execução por ID
   */
  async getAutomationExecution(id: number): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>(
      `${Config.API_BASE_URL}/automation/executions/${id}`,
      { method: 'GET' }
    );
  }

  /**
   * Cancela execução ativa
   */
  async cancelAutomationExecution(id: number): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>(
      `${Config.API_BASE_URL}/automation/executions/${id}/cancel`,
      { method: 'POST' }
    );
  }

  /**
   * Inicia execução manual de um fluxo
   */
  async startAutomationExecution(flowId: number, data: { phone_number: string; contact_name?: string; context?: any }): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>(
      `${Config.API_BASE_URL}/automation/flows/${flowId}/execute`,
      {
        method: 'POST',
        body: JSON.stringify(data)
      }
    );
  }

  /**
   * Obtém métricas de automação
   */
  async getAutomationMetrics(params?: { period?: string; periodo?: string; flow_id?: number }): Promise<ApiResponse<any>> {
    // O backend espera o parâmetro `period`; normalizamos `periodo` (legado) para `period`.
    const normalized: Record<string, any> = { ...(params || {}) };
    if (normalized.periodo !== undefined && normalized.period === undefined) {
      normalized.period = normalized.periodo;
    }
    delete normalized.periodo;
    const queryString = new URLSearchParams(
      Object.entries(normalized)
        .filter(([_, value]) => value !== undefined)
        .map(([key, value]) => [key, String(value)])
    ).toString();
    return this.request<ApiResponse<any>>(
      `${Config.API_BASE_URL}/automation/metrics${queryString ? `?${queryString}` : ''}`,
      { method: 'GET' }
    );
  }

  /**
   * Lista logs de auditoria de automação
   */
  async getAutomationAuditLogs(params?: { page?: number; limit?: number; action?: string; user_id?: number; flow_id?: number; startDate?: string; endDate?: string }): Promise<ApiResponse<any>> {
    const queryString = params
      ? new URLSearchParams(
          Object.entries(params)
            .filter(([_, value]) => value !== undefined)
            .map(([key, value]) => [key, String(value)])
        ).toString()
      : '';
    return this.request<ApiResponse<any>>(
      `${Config.API_BASE_URL}/automation/audit${queryString ? `?${queryString}` : ''}`,
      { method: 'GET' }
    );
  }

  /**
   * Exporta logs de auditoria de automação
   */
  async exportAutomationAuditLogs(params?: { format?: string; startDate?: string; endDate?: string }): Promise<Blob> {
    const queryString = params
      ? new URLSearchParams(
          Object.entries(params)
            .filter(([_, value]) => value !== undefined)
            .map(([key, value]) => [key, String(value)])
        ).toString()
      : '';
    const response = await fetch(
      `${Config.API_BASE_URL}/automation/audit/export${queryString ? `?${queryString}` : ''}`,
      {
        method: 'GET',
        headers: getDefaultHeaders()
      }
    );
    return response.blob();
  }

  // =============================================
  // PRODUTIVIDADE
  // =============================================

  /**
   * Lista métricas de produtividade
   */
  async getProductivityMetrics(filters: PaginationParams & { periodo?: string; veiculo_id?: number; motorista_id?: number; status?: string } = {}): Promise<ApiResponse<{ metrics: any[]; pagination: any }>> {
    const queryString = new URLSearchParams(
      Object.entries(filters)
        .filter(([_, value]) => value !== undefined)
        .map(([key, value]) => [key, String(value)])
    ).toString();

    return this.request<ApiResponse<{ metrics: any[]; pagination: any }>>(
      `${Config.API_BASE_URL}/productivity${queryString ? `?${queryString}` : ''}`,
      { method: 'GET' }
    );
  }

  /**
   * Obtém métrica de produtividade por ID
   */
  async getProductivityMetric(id: number): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>(
      `${Config.API_BASE_URL}/productivity/${id}`,
      { method: 'GET' }
    );
  }

  /**
   * Cria nova métrica de produtividade
   */
  async createProductivityMetric(metricData: any): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>(
      `${Config.API_BASE_URL}/productivity`,
      {
        method: 'POST',
        body: JSON.stringify(metricData)
      }
    );
  }

  /**
   * Atualiza métrica de produtividade
   */
  async updateProductivityMetric(id: number, updateData: any): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>(
      `${Config.API_BASE_URL}/productivity/${id}`,
      {
        method: 'PUT',
        body: JSON.stringify(updateData)
      }
    );
  }

  /**
   * Deleta métrica de produtividade
   */
  async deleteProductivityMetric(id: number): Promise<ApiResponse<{ message: string }>> {
    return this.request<ApiResponse<{ message: string }>>(
      `${Config.API_BASE_URL}/productivity/${id}`,
      { method: 'DELETE' }
    );
  }

  // =============================================
  // MANUTENÇÕES PREVENTIVAS
  // =============================================

  /**
   * Lista manutenções preventivas
   */
  async getPreventiveMaintenances(filters: PaginationParams & { veiculo_id?: number; status?: string; prioridade?: string } = {}): Promise<ApiResponse<{ maintenances: any[]; pagination: any }>> {
    const queryString = new URLSearchParams(
      Object.entries(filters)
        .filter(([_, value]) => value !== undefined)
        .map(([key, value]) => [key, String(value)])
    ).toString();

    return this.request<ApiResponse<{ maintenances: any[]; pagination: any }>>(
      `${Config.API_BASE_URL}/preventive-maintenance${queryString ? `?${queryString}` : ''}`,
      { method: 'GET' }
    );
  }

  /**
   * Obtém manutenção preventiva por ID
   */
  async getPreventiveMaintenance(id: number): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>(
      `${Config.API_BASE_URL}/preventive-maintenance/${id}`,
      { method: 'GET' }
    );
  }

  /**
   * Cria nova manutenção preventiva
   */
  async createPreventiveMaintenance(maintenanceData: any): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>(
      `${Config.API_BASE_URL}/preventive-maintenance`,
      {
        method: 'POST',
        body: JSON.stringify(maintenanceData)
      }
    );
  }

  /**
   * Atualiza manutenção preventiva
   */
  async updatePreventiveMaintenance(id: number, updateData: any): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>(
      `${Config.API_BASE_URL}/preventive-maintenance/${id}`,
      {
        method: 'PUT',
        body: JSON.stringify(updateData)
      }
    );
  }

  /**
   * Deleta manutenção preventiva
   */
  async deletePreventiveMaintenance(id: number): Promise<ApiResponse<{ message: string }>> {
    return this.request<ApiResponse<{ message: string }>>(
      `${Config.API_BASE_URL}/preventive-maintenance/${id}`,
      { method: 'DELETE' }
    );
  }

  // =============================================
  // MANUTENÇÕES CORRETIVAS
  // =============================================

  /**
   * Lista manutenções corretivas
   */
  async getCorrectiveMaintenances(filters: PaginationParams & { veiculo_id?: number; status?: string; prioridade?: string; tipo_problema?: string } = {}): Promise<ApiResponse<{ maintenances: any[]; pagination: any }>> {
    const queryString = new URLSearchParams(
      Object.entries(filters)
        .filter(([_, value]) => value !== undefined)
        .map(([key, value]) => [key, String(value)])
    ).toString();

    return this.request<ApiResponse<{ maintenances: any[]; pagination: any }>>(
      `${Config.API_BASE_URL}/corrective-maintenance${queryString ? `?${queryString}` : ''}`,
      { method: 'GET' }
    );
  }

  /**
   * Obtém manutenção corretiva por ID
   */
  async getCorrectiveMaintenance(id: number): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>(
      `${Config.API_BASE_URL}/corrective-maintenance/${id}`,
      { method: 'GET' }
    );
  }

  /**
   * Cria nova manutenção corretiva
   */
  async createCorrectiveMaintenance(maintenanceData: any): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>(
      `${Config.API_BASE_URL}/corrective-maintenance`,
      {
        method: 'POST',
        body: JSON.stringify(maintenanceData)
      }
    );
  }

  /**
   * Atualiza manutenção corretiva
   */
  async updateCorrectiveMaintenance(id: number, updateData: any): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>(
      `${Config.API_BASE_URL}/corrective-maintenance/${id}`,
      {
        method: 'PUT',
        body: JSON.stringify(updateData)
      }
    );
  }

  /**
   * Deleta manutenção corretiva
   */
  async deleteCorrectiveMaintenance(id: number): Promise<ApiResponse<{ message: string }>> {
    return this.request<ApiResponse<{ message: string }>>(
      `${Config.API_BASE_URL}/corrective-maintenance/${id}`,
      { method: 'DELETE' }
    );
  }

  // =============================================
  // COMPLIANCE
  // =============================================

  async getComplianceResumo(): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>(`${Config.API_BASE_URL}/compliance/resumo`, { method: 'GET' });
  }

  async getComplianceVencimentos(tipo?: string): Promise<ApiResponse<any[]>> {
    const qs = tipo ? `?tipo=${tipo}` : '';
    return this.request<ApiResponse<any[]>>(`${Config.API_BASE_URL}/compliance/vencimentos${qs}`, { method: 'GET' });
  }

  // =============================================
  // DASHBOARD KPIS
  // =============================================

  async getDashboardKpis(): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>(`${Config.API_BASE_URL}/dashboard/kpis`, { method: 'GET' });
  }

  // =============================================
  // TCO (CUSTO TOTAL DE PROPRIEDADE)
  // =============================================

  async getTcoRanking(): Promise<ApiResponse<any[]>> {
    return this.request<ApiResponse<any[]>>(`${Config.API_BASE_URL}/tco/ranking`, { method: 'GET' });
  }

  async getTcoVeiculo(veiculoId: number): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>(`${Config.API_BASE_URL}/tco/${veiculoId}`, { method: 'GET' });
  }

  // =============================================
  // PNEUS
  // =============================================

  async getPneus(filters: PaginationParams & { status?: string; veiculo_id?: number; marca?: string } = {}): Promise<ApiResponse<{ pneus: any[]; pagination: any }>> {
    const queryString = new URLSearchParams(
      Object.entries(filters).filter(([_, v]) => v !== undefined).map(([k, v]) => [k, String(v)])
    ).toString();
    return this.request<ApiResponse<{ pneus: any[]; pagination: any }>>(
      `${Config.API_BASE_URL}/pneus${queryString ? `?${queryString}` : ''}`,
      { method: 'GET' }
    );
  }

  async getPneu(id: number): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>(`${Config.API_BASE_URL}/pneus/${id}`, { method: 'GET' });
  }

  async createPneu(data: any): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>(`${Config.API_BASE_URL}/pneus`, { method: 'POST', body: JSON.stringify(data) });
  }

  async updatePneu(id: number, data: any): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>(`${Config.API_BASE_URL}/pneus/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  async deletePneu(id: number): Promise<ApiResponse<{ message: string }>> {
    return this.request<ApiResponse<{ message: string }>>(`${Config.API_BASE_URL}/pneus/${id}`, { method: 'DELETE' });
  }

  async getPneuMedicoes(id: number): Promise<ApiResponse<any[]>> {
    return this.request<ApiResponse<any[]>>(`${Config.API_BASE_URL}/pneus/${id}/medicoes`, { method: 'GET' });
  }

  async createPneuMedicao(id: number, data: any): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>(`${Config.API_BASE_URL}/pneus/${id}/medicoes`, { method: 'POST', body: JSON.stringify(data) });
  }

  async getPneuMovimentacoes(id: number): Promise<ApiResponse<any[]>> {
    return this.request<ApiResponse<any[]>>(`${Config.API_BASE_URL}/pneus/${id}/movimentacoes`, { method: 'GET' });
  }

  async createPneuMovimentacao(id: number, data: any): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>(`${Config.API_BASE_URL}/pneus/${id}/movimentacoes`, { method: 'POST', body: JSON.stringify(data) });
  }

  async getPneuAnalise(id: number): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>(`${Config.API_BASE_URL}/pneus/${id}/analise`, { method: 'GET' });
  }

  async getPneusPorVeiculo(veiculoId: number): Promise<ApiResponse<any[]>> {
    return this.request<ApiResponse<any[]>>(`${Config.API_BASE_URL}/pneus/veiculo/${veiculoId}`, { method: 'GET' });
  }

  // =============================================
  // MÉTODOS DE NOTIFICAÇÕES
  // =============================================

  /**
   * Obtém lista de notificações do usuário
   */
  async getNotifications(filters: { limit?: number; offset?: number; unreadOnly?: boolean } = {}): Promise<ApiResponse<any[]>> {
    // Converter boolean para string para compatibilidade com query params
    const queryParams: Record<string, string | number | undefined> = {
      limit: filters.limit,
      offset: filters.offset,
      unreadOnly: filters.unreadOnly !== undefined ? (filters.unreadOnly ? 'true' : 'false') : undefined
    };
    return this.get<ApiResponse<any[]>>('NOTIFICATIONS', {}, queryParams);
  }

  /**
   * Obtém contagem de notificações não lidas
   */
  async getUnreadNotificationsCount(): Promise<ApiResponse<{ count: number }>> {
    return this.get<ApiResponse<{ count: number }>>('NOTIFICATION_UNREAD_COUNT');
  }

  /**
   * Marca notificação como lida
   */
  async markNotificationAsRead(id: number): Promise<ApiResponse<any>> {
    return this.put<ApiResponse<any>>('NOTIFICATION_MARK_READ', {}, { id });
  }

  /**
   * Marca todas notificações como lidas
   */
  async markAllNotificationsAsRead(): Promise<ApiResponse<any>> {
    return this.put<ApiResponse<any>>('NOTIFICATION_MARK_ALL_READ', {});
  }

  /**
   * Deleta notificação
   */
  async deleteNotification(id: number): Promise<ApiResponse<{ message: string }>> {
    return this.delete<ApiResponse<{ message: string }>>('NOTIFICATION_BY_ID', { id });
  }

  /**
   * Deleta todas notificações
   */
  async deleteAllNotifications(): Promise<ApiResponse<any>> {
    return this.delete<ApiResponse<any>>('NOTIFICATIONS');
  }

  /**
   * Cria notificação de teste (apenas para desenvolvimento)
   */
  async createTestNotification(data: { tipo?: string; titulo?: string; mensagem?: string; link?: string } = {}): Promise<ApiResponse<any>> {
    return this.post<ApiResponse<any>>('NOTIFICATION_TEST', data);
  }

  // =============================================
  // PREFERÊNCIAS DE TABELA DO USUÁRIO
  // =============================================

  /**
   * Busca preferências de tabela do usuário para uma página específica
   */
  async getUserTablePreferences(pageName: string): Promise<ApiResponse<{ preferences: any }>> {
    return this.get<ApiResponse<{ preferences: any }>>('USER_TABLE_PREFERENCES', { pageName });
  }

  /**
   * Salva preferências de tabela do usuário para uma página específica
   */
  async saveUserTablePreferences(pageName: string, preferences: any): Promise<ApiResponse<{ preferences: any }>> {
    return this.post<ApiResponse<{ preferences: any }>>('USER_TABLE_PREFERENCES_SAVE', { preferences }, { pageName });
  }

  /**
   * Deleta preferências de tabela do usuário para uma página específica
   */
  async deleteUserTablePreferences(pageName: string): Promise<ApiResponse<{ message: string }>> {
    return this.delete<ApiResponse<{ message: string }>>('USER_TABLE_PREFERENCES_DELETE', { pageName });
  }

  // =============================================
  // TEMPLATES DE AUTOMAÇÃO
  // =============================================

  /**
   * Lista templates de automação
   */
  async listAutomationTemplates(params?: { category?: string; search?: string; tags?: string }): Promise<ApiResponse<any>> {
    const queryString = params
      ? new URLSearchParams(
          Object.entries(params)
            .filter(([_, value]) => value !== undefined)
            .map(([key, value]) => [key, String(value)])
        ).toString()
      : '';
    return this.request<ApiResponse<any>>(
      `${Config.API_BASE_URL}/automation/templates${queryString ? `?${queryString}` : ''}`,
      { method: 'GET' }
    );
  }

  /**
   * Lista templates populares
   */
  async getPopularTemplates(): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>(
      `${Config.API_BASE_URL}/automation/templates/popular`,
      { method: 'GET' }
    );
  }

  /**
   * Lista categorias de templates
   */
  async getTemplateCategories(): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>(
      `${Config.API_BASE_URL}/automation/templates/categories`,
      { method: 'GET' }
    );
  }

  /**
   * Obtém template por ID
   */
  async getAutomationTemplate(id: number): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>(
      `${Config.API_BASE_URL}/automation/templates/${id}`,
      { method: 'GET' }
    );
  }

  /**
   * Usa template para criar novo fluxo
   */
  async useAutomationTemplate(id: number): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>(
      `${Config.API_BASE_URL}/automation/templates/${id}/use`,
      { method: 'POST' }
    );
  }

  /**
   * Lista templates builtin
   */
  async getBuiltinTemplates(): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>(
      `${Config.API_BASE_URL}/automation/templates/builtin`,
      { method: 'GET' }
    );
  }

  /**
   * Obtém categorias de templates builtin
   */
  async getBuiltinTemplateCategories(): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>(
      `${Config.API_BASE_URL}/automation/templates/builtin/categories`,
      { method: 'GET' }
    );
  }

  /**
   * Obtém template builtin por ID
   */
  async getBuiltinTemplate(templateId: string): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>(
      `${Config.API_BASE_URL}/automation/templates/builtin/${templateId}`,
      { method: 'GET' }
    );
  }

  /**
   * Cria fluxo a partir de template builtin
   */
  async createFlowFromBuiltinTemplate(templateId: string, data?: { name?: string; description?: string }): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>(
      `${Config.API_BASE_URL}/automation/templates/builtin/${templateId}/create-flow`,
      {
        method: 'POST',
        body: JSON.stringify(data || {})
      }
    );
  }

  // =============================================
  // VARIÁVEIS GLOBAIS DE AUTOMAÇÃO
  // =============================================

  /**
   * Lista variáveis globais
   */
  async listGlobalVariables(): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>(
      `${Config.API_BASE_URL}/automation/variables`,
      { method: 'GET' }
    );
  }

  /**
   * Obtém variável global por chave
   */
  async getGlobalVariable(chave: string): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>(
      `${Config.API_BASE_URL}/automation/variables/${chave}`,
      { method: 'GET' }
    );
  }

  /**
   * Cria variável global
   */
  async createGlobalVariable(data: { chave: string; valor: any; tipo?: string; descricao?: string; is_secret?: boolean }): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>(
      `${Config.API_BASE_URL}/automation/variables`,
      {
        method: 'POST',
        body: JSON.stringify(data)
      }
    );
  }

  /**
   * Atualiza variável global
   */
  async updateGlobalVariable(chave: string, data: { valor?: any; descricao?: string; is_secret?: boolean }): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>(
      `${Config.API_BASE_URL}/automation/variables/${chave}`,
      {
        method: 'PUT',
        body: JSON.stringify(data)
      }
    );
  }

  /**
   * Define valor de variável global (shorthand)
   */
  async setGlobalVariable(chave: string, valor: any): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>(
      `${Config.API_BASE_URL}/automation/variables/${chave}/set`,
      {
        method: 'POST',
        body: JSON.stringify({ valor })
      }
    );
  }

  /**
   * Deleta variável global
   */
  async deleteGlobalVariable(chave: string): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>(
      `${Config.API_BASE_URL}/automation/variables/${chave}`,
      { method: 'DELETE' }
    );
  }

  /**
   * Atualiza múltiplas variáveis de uma vez
   */
  async batchUpdateGlobalVariables(variables: Array<{ chave: string; valor: any }>): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>(
      `${Config.API_BASE_URL}/automation/variables/batch`,
      {
        method: 'POST',
        body: JSON.stringify({ variables })
      }
    );
  }

  // =============================================
  // WEBHOOKS DE AUTOMAÇÃO
  // =============================================

  /**
   * Lista webhooks configurados
   */
  async listAutomationWebhooks(): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>(
      `${Config.API_BASE_URL}/automation/webhooks`,
      { method: 'GET' }
    );
  }

  /**
   * Obtém estatísticas de webhooks
   */
  async getWebhookStats(): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>(
      `${Config.API_BASE_URL}/automation/webhooks/stats`,
      { method: 'GET' }
    );
  }

  /**
   * Obtém webhook por ID
   */
  async getAutomationWebhook(id: number): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>(
      `${Config.API_BASE_URL}/automation/webhooks/${id}`,
      { method: 'GET' }
    );
  }

  /**
   * Lista entregas de um webhook
   */
  async getWebhookDeliveries(id: number): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>(
      `${Config.API_BASE_URL}/automation/webhooks/${id}/deliveries`,
      { method: 'GET' }
    );
  }

  /**
   * Cria novo webhook
   */
  async createAutomationWebhook(data: { nome: string; url: string; eventos: string[]; headers?: any; ativo?: boolean }): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>(
      `${Config.API_BASE_URL}/automation/webhooks`,
      {
        method: 'POST',
        body: JSON.stringify(data)
      }
    );
  }

  /**
   * Atualiza webhook
   */
  async updateAutomationWebhook(id: number, data: { nome?: string; url?: string; eventos?: string[]; headers?: any }): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>(
      `${Config.API_BASE_URL}/automation/webhooks/${id}`,
      {
        method: 'PUT',
        body: JSON.stringify(data)
      }
    );
  }

  /**
   * Ativa/desativa webhook
   */
  async toggleAutomationWebhook(id: number): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>(
      `${Config.API_BASE_URL}/automation/webhooks/${id}/toggle`,
      { method: 'POST' }
    );
  }

  /**
   * Testa webhook
   */
  async testAutomationWebhook(id: number): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>(
      `${Config.API_BASE_URL}/automation/webhooks/${id}/test`,
      { method: 'POST' }
    );
  }

  /**
   * Deleta webhook
   */
  async deleteAutomationWebhook(id: number): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>(
      `${Config.API_BASE_URL}/automation/webhooks/${id}`,
      { method: 'DELETE' }
    );
  }
}

// Exportar instância única do serviço
export const apiService = new ApiService();
